import React from 'react';

// ============================================
// Slot catalog helpers (findSlotArt / searchSlots — used site-wide) plus the
// classic bracket viewer for pre-Oct-2026 battles (the "Classic brackets"
// archive). The current battle UI lives in BattleParts / BattleBracket.
// ============================================

export const bFmtMoney = (n) => {
  const v = parseFloat(n) || 0;
  return '$' + v.toLocaleString(undefined, { maximumFractionDigits: v % 1 ? 2 : 0 });
};

export const bMult = (paid, buy) => {
  const b = parseFloat(buy) || 0, p = parseFloat(paid) || 0;
  if (b <= 0) return null;
  return p / b;
};

// ============================================
// Slot name matching — chat types things like "sweet banaza 1000",
// "holy hand greande 2 super" or "Magic Piggy OG Super". We strip the bonus-type
// wording people tack on, then score candidates on token overlap with typo
// tolerance, so near-misses still land on the right card art.
// ============================================
const bNorm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

// Wording that describes the BUY, not the slot. Stripped from the end of a pick.
const BUY_WORDS = new Set([
  'super', 'normal', 'bonus', 'buy', 'bonusbuy', 'og', 'legendary', 'epic', 'zupa',
  'scat', 'scats', 'scatter', 'scatters', 'spin', 'spins', 'free', 'freespins',
  'left', 'right', 'side', 'or', 'max', 'mini', 'x', 'lvl', 'level'
]);

// Damerau-Levenshtein with an early bail-out — enough to catch chat typos.
function editDistance(a, b, max) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2 = [], prev = [], cur = [];
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let best = cur[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cur[j] = Math.min(cur[j], prev2[j - 2] + 1); // adjacent transposition
      }
      if (cur[j] < best) best = cur[j];
    }
    if (best > max) return max + 1;
    for (let j = 0; j <= b.length; j++) { prev2[j] = prev[j]; prev[j] = cur[j]; }
  }
  return prev[b.length];
}

// How well one typed token matches a catalog token (0 = no match, 1 = exact)
function tokenScore(q, c) {
  if (q === c) return 1;
  if (q.length >= 3 && (c.startsWith(q) || q.startsWith(c))) return 0.9;
  const len = Math.max(q.length, c.length);
  if (len >= 4 && editDistance(q, c, 1) <= 1) return 0.82;
  if (len >= 7 && editDistance(q, c, 2) <= 2) return 0.66;
  return 0;
}

// A token is "optional" when it describes the buy rather than the slot
// (trailing "super", "3 scat", "left side") or is a bare number. Optional
// tokens never count against a match, but matching them boosts the score —
// so "gates of olympus super scatter" still beats plain "Gates of Olympus",
// and "holy hand grenade 2" beats "Holy Hand Grenade".
function isOptionalToken(t, i, tokens) {
  if (/^\d{1,4}$/.test(t)) return true;
  if (!BUY_WORDS.has(t)) return false;
  // Only treat buy words as optional once the name has started
  return i > 0;
}

// Normalised catalog index, rebuilt only when the catalog reference changes
let _idxSrc = null, _idx = null;
function slotIndex(catalog) {
  if (_idxSrc === catalog && _idx) return _idx;
  _idxSrc = catalog;
  _idx = (catalog || []).map(s => {
    const n = bNorm(s.name);
    return { slot: s, norm: n, compact: n.replace(/ /g, ''), tokens: n.split(' ').filter(Boolean) };
  });
  return _idx;
}

// Autocomplete search over the catalog, punctuation- and spacing-insensitive:
// "dead mans bounty" finds "Dead Man's Bounty: Hold and Win", "link merge" finds
// "Break Away Candy Link&Merge". Ranked: name starts with the query, then query
// appears anywhere, then every typed word prefixes some word in the name.
// Slots Shuffle has delisted are never suggested (findSlotArt still sees them).
export function searchSlots(slotCatalog, query, limit = 8) {
  const qNorm = bNorm(query);
  const qCompact = qNorm.replace(/ /g, '');
  if (qCompact.length < 2 || !slotCatalog || !slotCatalog.length) return [];
  const qTokens = qNorm.split(' ').filter(Boolean);
  const qRaw = (query || '').trim().toLowerCase();
  const starts = [], contains = [], tokenHits = [];
  for (const e of slotIndex(slotCatalog)) {
    if (e.slot.legacy) continue;
    // Hide only an exact literal match (nothing left to suggest). A normalized
    // match still shows — picking it fills in punctuation, provider, and art.
    if (e.slot.name.toLowerCase() === qRaw) continue;
    if (e.compact.startsWith(qCompact)) {
      if (starts.push(e.slot) >= limit) break; // top tier full — nothing can outrank it
    } else if (e.compact.includes(qCompact)) {
      if (contains.length < limit) contains.push(e.slot);
    } else if (qTokens.length > 1 && tokenHits.length < limit
               && qTokens.every(qt => e.tokens.some(t => t.startsWith(qt)))) {
      tokenHits.push(e.slot);
    }
  }
  return [...starts, ...contains, ...tokenHits].slice(0, limit);
}

const _matchCache = new Map();

export function findSlotArt(slotCatalog, slotName) {
  const q = bNorm(slotName);
  if (!q || q.length < 2 || !slotCatalog || !slotCatalog.length) return null;
  if (_matchCache.has(q)) return _matchCache.get(q);

  const idx = slotIndex(slotCatalog);
  let result = idx.find(e => e.norm === q)?.slot || null;

  if (!result) {
    const qTokens = q.split(' ').filter(Boolean);
    const required = qTokens.filter((t, i) => !isOptionalToken(t, i, qTokens));
    const reqSet = new Set(required);
    let best = null, bestScore = 0, bestMatched = 0;

    for (const e of idx) {
      if (!e.tokens.length) continue;
      const usedC = new Array(e.tokens.length).fill(false);
      let reqScore = 0, allScore = 0;

      for (const qt of qTokens) {
        let bestT = 0, bestJ = -1;
        for (let j = 0; j < e.tokens.length; j++) {
          if (usedC[j]) continue;
          const sc = tokenScore(qt, e.tokens[j]);
          if (sc > bestT) { bestT = sc; bestJ = j; }
        }
        if (bestJ >= 0 && bestT > 0) {
          usedC[bestJ] = true;
          allScore += bestT;
          if (reqSet.has(qt)) reqScore += bestT;
        }
      }
      if (!allScore) continue;

      const qCoverage = required.length ? reqScore / required.length : allScore / qTokens.length;
      const cCoverage = usedC.filter(Boolean).length / e.tokens.length;
      const detail = allScore / qTokens.length; // rewards matching the optional bits too
      let score = qCoverage * 0.70 + cCoverage * 0.20 + detail * 0.10;
      if (e.norm === q) score += 0.1;

      if (score > bestScore + 1e-9 || (Math.abs(score - bestScore) < 1e-9 && allScore > bestMatched)) {
        bestScore = score; bestMatched = allScore; best = e.slot;
      }
    }

    // Single-word picks need near-certainty; multi-word can be looser
    const threshold = required.length <= 1 ? 0.80 : 0.70;
    if (bestScore >= threshold) result = best;
  }

  if (_matchCache.size > 800) _matchCache.clear();
  _matchCache.set(q, result);
  return result;
}

export function roundLabel(round, matchesInRound, stopAt) {
  const isLast = matchesInRound === stopAt;
  if (isLast) return matchesInRound === 1 ? 'GRAND FINAL' : 'FINAL ROUND';
  if (stopAt === 1 && matchesInRound === 2) return 'SEMI-FINALS';
  if (stopAt === 1 && matchesInRound === 4) return 'QUARTER-FINALS';
  return `ROUND ${round}`;
}

// One side of a match card. slotName = the slot played in THIS match
// (per-match snapshot; falls back to the player's current slot)
function PlayerSide({ player, slotName, buy, paid, isWinner, isDone, art, size, alignRight }) {
  const lg = size === 'lg';
  const m = bMult(paid, buy);
  const nameColor = !isDone ? '#fff' : isWinner ? '#34d399' : '#71717a';
  const dim = isDone && !isWinner;

  const card = (
    <div style={{ position: 'relative', flexShrink: 0, opacity: dim ? 0.45 : 1, filter: dim ? 'grayscale(0.7)' : 'none' }}>
      {art?.image ? (
        <img src={art.image} alt="" style={{ width: lg ? '74px' : '46px', height: lg ? '104px' : '64px', objectFit: 'cover', borderRadius: lg ? '10px' : '7px', border: `2px solid ${isDone && isWinner ? '#22c55e' : 'rgba(255,255,255,0.15)'}`, display: 'block' }} />
      ) : (
        <div style={{ width: lg ? '74px' : '46px', height: lg ? '104px' : '64px', borderRadius: lg ? '10px' : '7px', border: '2px solid rgba(255,255,255,0.12)', background: 'linear-gradient(160deg, rgba(168,85,247,0.3), rgba(0,0,0,0.6))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: lg ? '1.6rem' : '1rem' }}>🎰</div>
      )}
      {isDone && isWinner && (
        <span style={{ position: 'absolute', top: '-8px', left: '-8px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', fontSize: lg ? '0.6rem' : '0.5rem', fontWeight: 900, letterSpacing: '1px', padding: '0.14rem 0.4rem', borderRadius: '6px', boxShadow: '0 2px 8px rgba(34,197,94,0.5)' }}>WIN</span>
      )}
      {player?.seed && (
        <span style={{ position: 'absolute', bottom: '-6px', right: '-6px', background: '#18181b', border: '1px solid rgba(255,255,255,0.2)', color: '#d4d4d8', fontSize: lg ? '0.6rem' : '0.52rem', fontWeight: 800, padding: '0.1rem 0.35rem', borderRadius: '6px' }}>#{player.seed}</span>
      )}
    </div>
  );

  const info = (
    <div style={{ minWidth: 0, flex: 1, textAlign: alignRight ? 'right' : 'left' }}>
      <div style={{ fontWeight: 800, fontSize: lg ? '1.15rem' : '0.76rem', color: nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{player?.display_name || '—'}</div>
      <div style={{ fontSize: lg ? '0.8rem' : '0.6rem', color: dim ? '#52525b' : '#a1a1aa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: lg ? '0.4rem' : '0.2rem' }}>{slotName || player?.slot_name || ''}</div>
      {buy > 0 && (
        <div style={{ fontSize: lg ? '0.78rem' : '0.62rem', color: dim ? '#52525b' : '#8b8b96', display: 'flex', gap: '0.6rem', justifyContent: alignRight ? 'flex-end' : 'flex-start', flexWrap: 'wrap' }}>
          <span>BUY <strong style={{ color: dim ? '#71717a' : '#fff' }}>{bFmtMoney(buy)}</strong></span>
          {isDone && (
            <span>PAID <strong style={{ color: isWinner ? '#34d399' : '#71717a' }}>{bFmtMoney(paid)}</strong></span>
          )}
        </div>
      )}
      {isDone && m !== null && (
        <span style={{
          display: 'inline-block', marginTop: lg ? '0.35rem' : '0.2rem',
          fontSize: lg ? '0.8rem' : '0.6rem', fontWeight: 900, padding: lg ? '0.18rem 0.6rem' : '0.1rem 0.4rem', borderRadius: '7px',
          background: isWinner ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${isWinner ? 'rgba(34,197,94,0.55)' : 'rgba(255,255,255,0.12)'}`,
          color: isWinner ? '#34d399' : '#71717a'
        }}>{m.toFixed(2)}x</span>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: lg ? '0.9rem' : '0.55rem', flex: 1, minWidth: 0, flexDirection: alignRight ? 'row-reverse' : 'row' }}>
      {card}
      {info}
    </div>
  );
}

// ============================================
// BracketViewer — explorable bracket map: drag to pan, scroll to zoom,
// auto-fits on open. Scales to any bracket size (32, 64, 128 players)
// while keeping the match cards readable.
// ============================================
export function BracketViewer({ battle, players, matches, slotCatalog, height = 520 }) {
  const containerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const [view, setView] = React.useState({ x: 24, y: 24, scale: 1 });
  const [dragging, setDragging] = React.useState(false);
  const dragRef = React.useRef(null);

  const playersById = {};
  for (const p of players || []) playersById[p.id] = p;
  const byRound = {};
  for (const m of matches || []) (byRound[m.round] = byRound[m.round] || []).push(m);
  const rounds = Object.keys(byRound).sort((a, b) => a - b).map(r => ({ round: parseInt(r, 10), ms: byRound[r] }));

  const clamp = (s) => Math.min(1.6, Math.max(0.2, s));

  const fit = React.useCallback(() => {
    const c = containerRef.current, inner = contentRef.current;
    if (!c || !inner || !inner.offsetWidth || !inner.offsetHeight) return;
    const pad = 28;
    const s = clamp(Math.min((c.clientWidth - pad * 2) / inner.offsetWidth, (c.clientHeight - pad * 2) / inner.offsetHeight, 1));
    setView({ scale: s, x: (c.clientWidth - inner.offsetWidth * s) / 2, y: (c.clientHeight - inner.offsetHeight * s) / 2 });
  }, []);
  React.useEffect(() => { const t = setTimeout(fit, 60); return () => clearTimeout(t); }, [fit, (matches || []).length]);

  // Wheel zoom centred on the cursor (manual listener — React's onWheel is passive)
  React.useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const onWheel = (e) => {
      e.preventDefault();
      setView(v => {
        const ns = clamp(v.scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12));
        const rect = c.getBoundingClientRect();
        const mx = e.clientX - rect.left, my = e.clientY - rect.top;
        return { scale: ns, x: mx - ((mx - v.x) / v.scale) * ns, y: my - ((my - v.y) / v.scale) * ns };
      });
    };
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => c.removeEventListener('wheel', onWheel);
  }, []);

  const startDrag = (cx, cy) => { dragRef.current = { sx: cx, sy: cy, ox: view.x, oy: view.y }; setDragging(true); };
  const moveDrag = (cx, cy) => {
    const d = dragRef.current;
    if (!d) return;
    setView(v => ({ ...v, x: d.ox + (cx - d.sx), y: d.oy + (cy - d.sy) }));
  };
  const endDrag = () => { dragRef.current = null; setDragging(false); };

  const zoomBy = (factor) => {
    const c = containerRef.current;
    if (!c) return;
    setView(v => {
      const ns = clamp(v.scale * factor);
      const mx = c.clientWidth / 2, my = c.clientHeight / 2;
      return { scale: ns, x: mx - ((mx - v.x) / v.scale) * ns, y: my - ((my - v.y) / v.scale) * ns };
    });
  };

  const ctrlBtn = { width: '32px', height: '32px', borderRadius: '9px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(14,12,20,0.9)', color: '#d4d4d8', fontSize: '0.95rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Outfit, sans-serif' };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', height: `${height}px`, overflow: 'hidden', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', background: 'radial-gradient(circle at 50% 30%, rgba(168,85,247,0.05), transparent 60%), rgba(0,0,0,0.3)', cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
      onMouseDown={e => { if (e.button === 0) startDrag(e.clientX, e.clientY); }}
      onMouseMove={e => moveDrag(e.clientX, e.clientY)}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchStart={e => { const t = e.touches[0]; if (t) startDrag(t.clientX, t.clientY); }}
      onTouchMove={e => { const t = e.touches[0]; if (t) moveDrag(t.clientX, t.clientY); }}
      onTouchEnd={endDrag}
      onDragStart={e => e.preventDefault()}
    >
      <div ref={contentRef} style={{ position: 'absolute', top: 0, left: 0, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: '0 0', display: 'flex', gap: '1.6rem', alignItems: 'stretch', width: 'max-content', padding: '4px' }}>
        {rounds.map(({ round, ms }) => {
          const roundTotal = battle ? Math.floor(battle.entrant_count / Math.pow(2, round)) : ms.length;
          const label = roundLabel(round, roundTotal, battle?.stop_at || 1);
          return (
            <div key={round} style={{ width: '330px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ textAlign: 'center', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '2px', color: battle && round === battle.current_round && battle.status === 'rolled' ? '#fbbf24' : '#8b8b96', marginBottom: '0.5rem' }}>
                {label}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: '0.6rem' }}>
                {ms.map(m => (
                  <MatchCard key={m.id} match={m} playersById={playersById} slotCatalog={slotCatalog} roundText={label} size="sm" />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', flexDirection: 'column', gap: '0.3rem', zIndex: 5 }}>
        <button style={ctrlBtn} onClick={e => { e.stopPropagation(); zoomBy(1.25); }} onMouseDown={e => e.stopPropagation()} title="Zoom in">＋</button>
        <button style={ctrlBtn} onClick={e => { e.stopPropagation(); zoomBy(1 / 1.25); }} onMouseDown={e => e.stopPropagation()} title="Zoom out">－</button>
        <button style={{ ...ctrlBtn, fontSize: '0.8rem' }} onClick={e => { e.stopPropagation(); fit(); }} onMouseDown={e => e.stopPropagation()} title="Fit whole bracket">⤢</button>
      </div>
      {/* Hint */}
      <div style={{ position: 'absolute', bottom: '10px', left: '12px', zIndex: 5, pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.28rem 0.7rem', borderRadius: '999px', background: 'rgba(14,12,20,0.85)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.64rem', fontWeight: 700, color: '#8b8b96', fontFamily: 'Outfit, sans-serif' }}>
        🖐️ Drag to explore · scroll to zoom
      </div>
    </div>
  );
}

// Full match card, screenshot-style. size: 'sm' (bracket grid) | 'lg' (widget spotlight)
export function MatchCard({ match, playersById, slotCatalog, roundText, size = 'sm', children }) {
  const lg = size === 'lg';
  const p1 = playersById[match.player1_id];
  const p2 = playersById[match.player2_id];
  const isDone = match.status === 'done';
  const isLive = match.status === 'live';
  const slot1 = match.p1_slot || p1?.slot_name;
  const slot2 = match.p2_slot || p2?.slot_name;
  const art1 = findSlotArt(slotCatalog, slot1);
  const art2 = findSlotArt(slotCatalog, slot2);

  return (
    <div style={{
      background: 'linear-gradient(180deg, rgba(18,18,24,0.97), rgba(10,10,14,0.97))',
      border: `1px solid ${isLive ? 'rgba(251,191,36,0.65)' : 'rgba(255,255,255,0.09)'}`,
      borderRadius: lg ? '16px' : '12px',
      boxShadow: isLive ? '0 0 28px rgba(251,191,36,0.25)' : '0 8px 26px rgba(0,0,0,0.45)',
      overflow: 'hidden'
    }}>
      {/* Header strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: lg ? '0.5rem 1rem' : '0.35rem 0.7rem', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: lg ? '0.72rem' : '0.58rem', fontWeight: 800, letterSpacing: '1.5px', color: '#d4d4d8' }}>
          <span style={{ width: lg ? '8px' : '6px', height: lg ? '8px' : '6px', borderRadius: '50%', background: isLive ? '#fbbf24' : '#a855f7', boxShadow: isLive ? '0 0 8px #fbbf24' : 'none' }} />
          {roundText}
        </span>
        <span style={{ fontSize: lg ? '0.72rem' : '0.58rem', fontWeight: 800, letterSpacing: '1.5px', color: isLive ? '#fbbf24' : '#71717a' }}>
          {isLive ? '● LIVE' : `MATCH ${match.match_number}`}
        </span>
      </div>
      {/* Body */}
      <div style={{ display: 'flex', alignItems: 'center', gap: lg ? '1rem' : '0.6rem', padding: lg ? '1.1rem 1.2rem' : '0.65rem 0.7rem' }}>
        <PlayerSide player={p1} slotName={slot1} buy={match.p1_buy} paid={match.p1_paid} isWinner={isDone && match.winner_id === match.player1_id} isDone={isDone} art={art1} size={size} alignRight={false} />
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem' }}>
          <span style={{ fontSize: lg ? '1.5rem' : '0.95rem', filter: isDone ? 'none' : 'grayscale(0.6)', opacity: isDone ? 1 : 0.7 }}>🏆</span>
          <span style={{ fontSize: lg ? '0.85rem' : '0.6rem', fontWeight: 900, letterSpacing: '2px', color: '#52525b', fontStyle: 'italic' }}>VS</span>
        </div>
        <PlayerSide player={p2} slotName={slot2} buy={match.p2_buy} paid={match.p2_paid} isWinner={isDone && match.winner_id === match.player2_id} isDone={isDone} art={art2} size={size} alignRight={true} />
      </div>
      {children}
    </div>
  );
}
